import React, { useCallback, useState } from 'react'
import * as R from 'ramda'
import { Subchapter } from 'types'
import styled from 'styled-components'
import { useParams } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Button } from 'examkrackers-components'
import BookContentsList from 'modules/books/components/BookContentsList'
import SpaceRepetitionModal from './aiSpaceRepetition/SpaceRepetitionModal'
import { useOpenSrqQuizFlow } from './aiSpaceRepetition/useOpenSrqQuizFlow'
import { useSrqStatus } from 'modules/quizDiagnostics/utils/srqStatusStore'
import {
  getBookByOriginalId,
  getIsBookContentsLoading,
  selectBookByOriginalId
} from 'modules/books/ducks/selectors'
import { refreshBookFragmentDataRoutine } from 'modules/books/ducks/actions'
import QuizModal from './aiSpaceRepetition/QuizModal'

interface SubchapterPreviewProps {
  subchapter: Subchapter
}

export const SubchapterPreview = ({
  subchapter
}: SubchapterPreviewProps): JSX.Element => {
  const order: string = R.pipe(R.propOr('', 'order'), R.toString)(subchapter)
  const title: string = R.propOr('', 'title')(subchapter)
  const subchapterId: string = R.propOr('', 'id')(subchapter)
  const studentBookChapterId: string = R.propOr('', 'chapter_id')(subchapter)
  const isLoading = useSelector(getIsBookContentsLoading)

  const [isSpaceRepetitionModalOpen, setIsSpaceRepetitionModalOpen] =
    useState(false)
  const [quizModalOpen, setQuizModalOpen] = useState(false)

  const params = useParams()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const bookId: string = R.propOr('', 'bookId')(params)
  const book = useSelector(state => getBookByOriginalId(state, bookId))
  const navBook = useSelector(state => selectBookByOriginalId(state, bookId))
  const tagColor = R.propOr('', 'tag_colour', book)
  const bookAbbreviation = R.pipe(R.propOr('', 'tag'), R.toUpper)(book)
  const srqEnabled: boolean =
    (R.propOr(false, 'srq_enabled', navBook) as boolean) &&
    (R.pathOr(false, ['book', 'srq_enabled'], navBook) as boolean)
  const hasQuizDueToday = R.propOr(
    false,
    'has_quiz_due_today',
    subchapter
  ) as boolean
  const sectionSrqRedDotsVisible =
    R.propOr(true, 'srq_red_dots_visible', subchapter) !== false
  const { srqRedDotsVisible } = useSrqStatus()
  const showQuizDueToday =
    srqRedDotsVisible && sectionSrqRedDotsVisible && hasQuizDueToday
  const hasEverTakenQuiz = R.propOr(
    false,
    'has_ever_taken_quiz',
    subchapter
  ) as boolean

  const chapterOrder: number = R.pipe(
    R.propOr('1', 'chapterOrder'),
    Number
  )(params)
  const partOrder: number = R.pipe(R.propOr('', 'partOrder'), Number)(params)

  const refreshSubchapters = useCallback(() => {
    dispatch(
      refreshBookFragmentDataRoutine({
        params: { bookId, chapterOrder, partOrder },
        fragmentName: 'subchapters'
      })
    )
  }, [bookId, chapterOrder, dispatch, partOrder])

  const handleQuizModalOpen = () => {
    setIsSpaceRepetitionModalOpen(false)
    setQuizModalOpen(true)
  }

  const handleCloseQuizModal = () => setQuizModalOpen(false)
  const handleOpenSpaceModal = useOpenSrqQuizFlow(
    () => setQuizModalOpen(true),
    () => setIsSpaceRepetitionModalOpen(true)
  )
  const handleCloseSpaceModal = () => setIsSpaceRepetitionModalOpen(false)

  return (
    <div id={`subchapter-anchor-${subchapterId}`}>
      <SubchapterContainer color={tagColor} isLoading={isLoading}>
        <div className='title-wrapper'>
          {chapterOrder}.{order} {title}
        </div>
      </SubchapterContainer>
      <div>
        <BookContentsList subchapter={subchapter} />
        {srqEnabled && (
          <AiButtonWrapper>
            <ButtonContainer $showQuizHighlight={!hasEverTakenQuiz}>
              <ButtonWithDot>
                <Button
                  color={showQuizDueToday ? 'orange' : 'secondary'}
                  onClick={handleOpenSpaceModal}
                >
                  {showQuizDueToday
                    ? t('aiSpaceRepetition.buttonDueToday')
                    : t('aiSpaceRepetition.button')}
                </Button>
                {showQuizDueToday ? <DueTodayDot aria-hidden /> : null}
              </ButtonWithDot>
              {!hasEverTakenQuiz ? (
                <NoQuizCompletedText>
                  {t('aiSpaceRepetition.noQuizCompletedYet')}
                </NoQuizCompletedText>
              ) : null}
            </ButtonContainer>
          </AiButtonWrapper>
        )}
      </div>

      <br />
      {isSpaceRepetitionModalOpen && (
        <SpaceRepetitionModal
          isOpen={isSpaceRepetitionModalOpen}
          onClose={handleCloseSpaceModal}
          setQuizModalOpen={handleQuizModalOpen}
        />
      )}
      {quizModalOpen && (
        <QuizModal
          isOpen={quizModalOpen}
          onClose={handleCloseQuizModal}
          onQuizCompleted={refreshSubchapters}
          studentBookSubchapterId={subchapterId}
          studentBookChapterId={studentBookChapterId}
          bookAbbreviation={bookAbbreviation}
          chapterOrder={chapterOrder}
          sectionOrder={order}
          sectionTitle={title}
        />
      )}
    </div>
  )
}

const SubchapterContainer = styled.div`
  display: inline-block;
  margin-top: 1rem;
  position: relative;
  opacity: ${({ isLoading }) => (isLoading ? 0 : 1)};
  transition: all 100ms ${({ theme }) => theme.transitions.easing.easeInOut};
  width: 100%;

  .title-wrapper {
    display: inline-block;
    transition: all 100ms ${({ theme }) => theme.transitions.easing.easeInOut};
    font-weight: bold;
    font-size: 18px;
    line-height: 19px;
    color: ${({ theme }) => theme.colors.main.heading};
    max-width: 535px;
    margin-bottom: 7px;
    padding-bottom: 3px;
    width: 100%;
    border-bottom: 3px solid
      ${({ theme, color }) => theme.colors.tags[color].backgroundActive};
  }
`

const AiButtonWrapper = styled.div`
  margin-top: 1.2rem;
  margin-bottom: 1rem;
  width: 510px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const ButtonContainer = styled.div<{ $showQuizHighlight: boolean }>`
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: ${({ $showQuizHighlight }) => ($showQuizHighlight ? '252px' : 'auto')};
  height: ${({ $showQuizHighlight }) => ($showQuizHighlight ? '74px' : 'auto')};
  border-radius: ${({ $showQuizHighlight }) =>
    $showQuizHighlight ? '5px' : '0'};
  padding: ${({ $showQuizHighlight }) =>
    $showQuizHighlight ? '8px 12px' : '0'};
  background: ${({ $showQuizHighlight }) =>
    $showQuizHighlight ? '#fad3d38a' : 'transparent'};
`

const ButtonWithDot = styled.div`
  position: relative;
  display: inline-flex;

  button {
    width: 236px;
    height: 41px;
  }
`

const NoQuizCompletedText = styled.p`
  margin: 4px 0 0;
  padding: 0;
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
  text-align: center;
  color: #a51417;
`

const DueTodayDot = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ea4335;
  z-index: 2;
  pointer-events: none;
`

export default SubchapterPreview
